// components/StateViewer/AdvancedStateViewer.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

const AdvancedStateViewer = () => {
  const state = useSelector(state => state);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [selectedSlice, setSelectedSlice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('slices');
  const [expandedPaths, setExpandedPaths] = useState(new Set());
  const previousState = useRef({});

  // Track expanded paths per slice for tree view
  const togglePath = (path) => {
    const newExpanded = new Set(expandedPaths);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedPaths(newExpanded);
  };

  // Track state changes
  useEffect(() => {
    previousState.current = state;
  }, [state]);

  // Safe JSON stringify for circular references
  const safeStringify = (obj, indent = 2) => {
    const cache = new Set();
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (cache.has(value)) {
          return '[Circular]';
        }
        cache.add(value);
      }
      return value;
    }, indent);
  };

  // Find differences between states
  const findDifferences = useMemo(() => {
    const diffs = [];
    const prev = previousState.current;
    const current = state;

    const compare = (prevObj, currObj, path = '') => {
      if (!prevObj || !currObj) return;

      const allKeys = new Set([
        ...Object.keys(prevObj || {}),
        ...Object.keys(currObj || {})
      ]);

      allKeys.forEach(key => {
        const currentPath = path ? `${path}.${key}` : key;
        
        if (prevObj[key] === undefined && currObj[key] !== undefined) {
          diffs.push({
            path: currentPath,
            oldValue: undefined,
            newValue: currObj[key],
            changedAt: new Date().toLocaleTimeString()
          });
        } else if (prevObj[key] !== undefined && currObj[key] === undefined) {
          diffs.push({
            path: currentPath,
            oldValue: prevObj[key],
            newValue: undefined,
            changedAt: new Date().toLocaleTimeString()
          });
        } else if (typeof prevObj[key] === 'object' && typeof currObj[key] === 'object') {
          compare(prevObj[key], currObj[key], currentPath);
        } else if (prevObj[key] !== currObj[key]) {
          diffs.push({
            path: currentPath,
            oldValue: prevObj[key],
            newValue: currObj[key],
            changedAt: new Date().toLocaleTimeString()
          });
        }
      });
    };

    compare(prev, current);
    return diffs.slice(0, 50); // Limit to 50 changes
  }, [state]);

  // TreeView Component (moved outside render function)
  const TreeView = ({ data, basePath = '' }) => {
    if (!data || typeof data !== 'object') {
      return (
        <div style={{ padding: '5px 0', color: '#ff6b6b' }}>
          Invalid data
        </div>
      );
    }

    const entries = Object.entries(data);
    
    if (entries.length === 0) {
      return (
        <div style={{ padding: '5px 0', color: '#aaa' }}>
          Empty object
        </div>
      );
    }

    return entries.map(([key, value]) => {
      const isObject = value && typeof value === 'object';
      const isArray = Array.isArray(value);
      const currentPath = basePath ? `${basePath}.${key}` : key;
      const isExpanded = expandedPaths.has(currentPath);

      return (
        <div key={currentPath}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '5px 0',
              borderBottom: '1px solid #333',
              cursor: isObject ? 'pointer' : 'default'
            }}
            onClick={() => isObject && togglePath(currentPath)}
          >
            {isObject ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePath(currentPath);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#4dabf7',
                  cursor: 'pointer',
                  marginRight: '5px',
                  width: '20px'
                }}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            ) : (
              <span style={{ marginLeft: '25px' }}>•</span>
            )}
            <span style={{ 
              fontWeight: 'bold', 
              color: isArray ? '#cc5de8' : '#e67700',
              marginRight: '10px'
            }}>
              {key}:
            </span>
            <span style={{ 
              color: typeof value === 'string' ? '#5c940d' : 
                    typeof value === 'number' ? '#1971c2' :
                    typeof value === 'boolean' ? '#f08c00' : 
                    value === null ? '#ff6b6b' : '#868e96'
            }}>
              {!isObject ? (
                typeof value === 'string' ? `"${value}"` : 
                value === null ? 'null' : 
                JSON.stringify(value)
              ) : isArray ? (
                `[${value.length} items]`
              ) : (
                `{${Object.keys(value).length} keys}`
              )}
            </span>
          </div>
          {isExpanded && isObject && (
            <div style={{ marginLeft: '20px' }}>
              <TreeView data={value} basePath={currentPath} />
            </div>
          )}
        </div>
      );
    });
  };

  // Render content based on view mode
  const renderContent = () => {
    switch (viewMode) {
      case 'tree':
        if (selectedSlice && state[selectedSlice]) {
          return (
            <div>
              <button
                onClick={() => {
                  setSelectedSlice(null);
                  setExpandedPaths(new Set());
                }}
                style={{
                  background: '#444',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '4px',
                  marginBottom: '15px',
                  cursor: 'pointer'
                }}
              >
                ← All Slices
              </button>
              <h4 style={{ color: 'white', marginBottom: '15px' }}>
                {selectedSlice}
              </h4>
              <div style={{
                background: '#2d2d2d',
                padding: '15px',
                borderRadius: '8px',
                maxHeight: '50vh',
                overflow: 'auto'
              }}>
                <TreeView data={state[selectedSlice]} />
              </div>
            </div>
          );
        }
        return (
          <div style={{ color: '#aaa', textAlign: 'center', padding: '40px' }}>
            Select a slice from the overview
          </div>
        );

      case 'raw':
        return (
          <pre style={{
            background: '#2d2d2d',
            padding: '20px',
            borderRadius: '8px',
            color: 'white',
            maxHeight: '50vh',
            overflow: 'auto',
            fontSize: '12px'
          }}>
            {safeStringify(selectedSlice ? state[selectedSlice] : state, 2)}
          </pre>
        );

      case 'diff':
        return (
          <div style={{ background: '#2d2d2d', padding: '20px', borderRadius: '8px' }}>
            <h4 style={{ color: 'white', marginBottom: '15px' }}>
              State Changes ({findDifferences.length})
            </h4>
            {findDifferences.length === 0 ? (
              <p style={{ color: '#aaa', textAlign: 'center' }}>
                No changes detected
              </p>
            ) : (
              <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
                {findDifferences.map((diff, index) => (
                  <div
                    key={index}
                    style={{
                      background: '#3d3d3d',
                      padding: '15px',
                      marginBottom: '10px',
                      borderRadius: '6px',
                      borderLeft: '4px solid #ff6b6b'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '5px'
                    }}>
                      <code style={{ color: '#4dabf7' }}>{diff.path}</code>
                      <span style={{ color: '#aaa', fontSize: '12px' }}>
                        {diff.changedAt}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '20px',
                      fontSize: '14px',
                      flexWrap: 'wrap'
                    }}>
                      <div>
                        <span style={{ color: '#ff6b6b' }}>Old:</span>
                        <code style={{ marginLeft: '5px', color: '#ffa8a8' }}>
                          {JSON.stringify(diff.oldValue) || 'undefined'}
                        </code>
                      </div>
                      <div>
                        <span style={{ color: '#5c940d' }}>New:</span>
                        <code style={{ marginLeft: '5px', color: '#b2f2bb' }}>
                          {JSON.stringify(diff.newValue) || 'undefined'}
                        </code>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'slices':
      default:
        return (
          <div style={{
            background: '#2d2d2d',
            padding: '20px',
            borderRadius: '8px',
            maxHeight: '50vh',
            overflow: 'auto'
          }}>
            <h4 style={{ color: 'white', marginBottom: '15px' }}>Slices:</h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '15px'
            }}>
              {Object.keys(state).map(sliceName => {
                const slice = state[sliceName];
                const sliceSize = safeStringify(slice).length;
                
                return (
                  <div
                    key={sliceName}
                    onClick={() => {
                      setSelectedSlice(sliceName);
                      setExpandedPaths(new Set());
                      setViewMode('tree');
                    }}
                    style={{
                      background: '#3d3d3d',
                      padding: '15px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: '1px solid #555',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#007acc'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#555'}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h5 style={{ margin: 0, color: '#4dabf7' }}>{sliceName}</h5>
                      <span style={{
                        background: '#555',
                        color: '#aaa',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '12px'
                      }}>
                        {typeof slice}
                      </span>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#aaa',
                      marginTop: '10px'
                    }}>
                      <div>Keys: {Object.keys(slice || {}).length}</div>
                      <div>Size: {
                        sliceSize > 1024 * 1024 ? `${(sliceSize / (1024 * 1024)).toFixed(2)} MB` :
                        sliceSize > 1024 ? `${(sliceSize / 1024).toFixed(2)} KB` : 
                        `${sliceSize} bytes`
                      }</div>
                      <div style={{ 
                        marginTop: '5px', 
                        color: slice ? '#51cf66' : '#ff6b6b' 
                      }}>
                        {slice ? '✓ Valid' : '✗ Empty/Invalid'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
    }
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '0',
      left: '0',
      right: '0',
      background: '#1a1a1a',
      borderTop: '2px solid #007acc',
      zIndex: 9999,
      maxHeight: isCollapsed ? '50px' : '80vh',
      transition: 'max-height 0.3s',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 20px',
        background: '#2d2d2d',
        cursor: 'pointer'
      }} onClick={() => setIsCollapsed(!isCollapsed)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span>🔍</span>
          <h3 style={{ margin: 0, color: 'white' }}>Redux State Inspector</h3>
          {findDifferences.length > 0 && (
            <span style={{
              background: '#ff6b6b',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '12px'
            }}>
              {findDifferences.length} changes
            </span>
          )}
          {selectedSlice && (
            <span style={{
              background: '#1971c2',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '12px'
            }}>
              {selectedSlice}
            </span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsCollapsed(!isCollapsed);
          }}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '20px',
            cursor: 'pointer'
          }}
        >
          {isCollapsed ? '▲' : '▼'}
        </button>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div style={{ padding: '20px', overflow: 'auto', maxHeight: 'calc(80vh - 60px)' }}>
          {/* Toolbar */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '20px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <input
              type="text"
              placeholder="Search in state..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: '200px',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #555',
                background: '#2d2d2d',
                color: 'white'
              }}
            />
            
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              style={{
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #555',
                background: '#2d2d2d',
                color: 'white'
              }}
            >
              <option value="slices">Slice Overview</option>
              <option value="tree">Tree View</option>
              <option value="raw">Raw JSON</option>
              <option value="diff">Changes</option>
            </select>

            <button
              onClick={() => {
                const data = selectedSlice ? state[selectedSlice] : state;
                console.log('State:', data);
                console.log('Full state:', state);
              }}
              style={{
                padding: '8px 15px',
                background: '#007acc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Log to Console
            </button>

            <button
              onClick={() => {
                const data = selectedSlice ? state[selectedSlice] : state;
                navigator.clipboard.writeText(safeStringify(data, 2));
              }}
              style={{
                padding: '8px 15px',
                background: '#5c940d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Copy JSON
            </button>

            {selectedSlice && (
              <button
                onClick={() => {
                  setSelectedSlice(null);
                  setExpandedPaths(new Set());
                  setViewMode('slices');
                }}
                style={{
                  padding: '8px 15px',
                  background: '#555',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Clear Selection
              </button>
            )}
          </div>

          {/* Main Content */}
          {renderContent()}
        </div>
      )}
    </div>
  );
};

export default AdvancedStateViewer;